// ===============================================================
// ✅ InvitePage.js — Invite Friends (Premium Hero Layout)
// path: src/components/InvitePage.js
// ===============================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import "./InvitePage.css";

import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import {
  FaCopy,
  FaShareAlt,
  FaWhatsapp,
  FaChevronLeft,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import BottomNav from "./BottomNav";
import TopBar2 from "./common/TopBar2";

import giftHeroImage from "../assets/invite-gift.webp";
import inviteClaimGift from "../assets/invite1-gift.webp";

const INVITE_HERO_IMAGE_WIDTH = 900;
const INVITE_HERO_IMAGE_HEIGHT = 375;

const APP_INVITE_BASE = "https://livora.app/invite";

async function fireInviteConfetti() {
  const { default: confetti } = await import("canvas-confetti");
  confetti({ particleCount: 120, spread: 75, origin: { y: 0.62 } });
  confetti({ particleCount: 70, angle: 60, spread: 55, origin: { x: 0, y: 0.65 } });
  confetti({ particleCount: 70, angle: 120, spread: 55, origin: { x: 1, y: 0.65 } });
}

export default function InvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const functions = useMemo(() => getFunctions(), []);
  const prevInviteCountRef = useRef(null);

  const [inviteCode, setInviteCode] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [inviteCount, setInviteCount] = useState(0);
  const [gender, setGender] = useState("");
  const [loading, setLoading] = useState(true);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    const loadInviteData = async () => {
      try {
        const user = auth.currentUser;

        if (!user) {
          navigate("/");
          return;
        }

        const snap = await getDoc(doc(db, "users", user.uid));

        if (snap.exists()) {
          setGender(String(snap.data()?.gender || "").toLowerCase());
        }

        const getMyInviteInfo = httpsCallable(functions, "getMyInviteInfo");
        const res = await getMyInviteInfo();

        const data = res.data || {};
        const code = String(data.inviteCode || "").trim();
        const count = Number(data.inviteCount || 0);

        setInviteCode(code);
        setInviteCount(count);
        setInviteLink(`${APP_INVITE_BASE}?ref=${encodeURIComponent(code)}`);
      } catch (e) {
        console.error("Load invites error:", e);
        alert(e?.message || t("invite.alert.load_error", { defaultValue: "Could not load invite data." }));
      } finally {
        setLoading(false);
      }
    };

    loadInviteData();
  }, [functions, navigate, t]);

  useEffect(() => {
    if (loading) return;

    if (prevInviteCountRef.current === null) {
      prevInviteCountRef.current = inviteCount;
      return;
    }

    if (inviteCount > prevInviteCountRef.current) {
      fireInviteConfetti().catch((err) => {
        console.error("Invite confetti error:", err);
      });
      setCelebrate(true);
      window.setTimeout(() => setCelebrate(false), 1200);
    }

    prevInviteCountRef.current = inviteCount;
  }, [inviteCount, loading]);

  const isFemale = gender === "female";

  const rewards = isFemale
    ? [
        { target: 10, label: t("invite.rewards.female_10_label", { defaultValue: "10 invites" }), reward: t("invite.rewards.tokens_30", { defaultValue: "30 Tokens" }) },
        { target: 20, label: t("invite.rewards.female_20_label", { defaultValue: "20 invites" }), reward: t("invite.rewards.tokens_30", { defaultValue: "30 Tokens" }) },
        { target: 30, label: t("invite.rewards.female_30_label", { defaultValue: "30 invites" }), reward: t("invite.rewards.tokens_30", { defaultValue: "30 Tokens" }) },
      ]
    : [
        { target: 5, label: t("invite.rewards.male_5_label", { defaultValue: "5 invites" }), reward: t("invite.rewards.coins_20", { defaultValue: "20 Coins" }) },
        { target: 10, label: t("invite.rewards.male_10_label", { defaultValue: "10 invites" }), reward: t("invite.rewards.coins_50", { defaultValue: "50 Coins" }) },
        { target: 20, label: t("invite.rewards.male_20_label", { defaultValue: "20 invites" }), reward: t("invite.rewards.coins_120", { defaultValue: "120 Coins" }) },
      ];

  const nextReward =
    rewards.find((r) => inviteCount < r.target) || rewards[rewards.length - 1];

  const progressTarget = nextReward?.target || 10;
  const progressPercent = Math.min(
    100,
    Math.round((inviteCount / progressTarget) * 100)
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      alert(t("invite.alert.copied", { defaultValue: "Invite link copied!" }));
    } catch {
      alert(t("invite.alert.copy_failed", { defaultValue: "Could not copy. Please copy the link manually." }));
    }
  };

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: t("invite.share.title", { defaultValue: "Join me on Livora" }),
          text: t("invite.share.text", {
            defaultValue: "Use my invite link and join Livora",
          }),
          url: inviteLink,
        });
      } else {
        await copyLink();
      }
    } catch (e) {
      console.log("Share cancelled/not supported:", e);
    }
  };

  const shareWhatsApp = () => {
    const text = t("invite.share.whatsapp_text", { defaultValue: "Join Livora through my invite link:\n{{link}}", link: inviteLink });

    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  if (loading) {
    return (
      <div className="invite-page">
        <TopBar2 title={t("invite.title", { defaultValue: "Invite friends" })} onBack={() => navigate(-1)} />

        <div className="invite-loading">
          <div className="loading-spinner" />
          <p>{t("invite.loading", { defaultValue: "Loading..." })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-page">
      <TopBar2 title={t("invite.title", { defaultValue: "Invite friends" })} onBack={() => navigate(-1)} />

      <div className={`invite-hero-card ${celebrate ? "celebrate" : ""}`}>
        <img
          src={giftHeroImage}
          alt={t("invite.gift_alt", { defaultValue: "Livora gift" })}
          className="invite-hero-image"
          width={INVITE_HERO_IMAGE_WIDTH}
          height={INVITE_HERO_IMAGE_HEIGHT}
          decoding="async"
          fetchPriority="high"
        />

        <div className="invite-hero-content">
          <div className="invite-count-row">
            <span className="invite-count-label">
              {t("invite.successful_count", { defaultValue: "Successful invites" })}
            </span>
            <span className="invite-count-divider">|</span>
            <span className="invite-count-number">{inviteCount}</span>
          </div>

          <span className="invite-reward-pill">
            {t("invite.next_reward", { defaultValue: "You are close to your next reward: {{reward}}", reward: nextReward?.reward })}
          </span>

          <div className="invite-progress-wrap">
            <div className="invite-progress-info">
              <span>{inviteCount}</span>
              <span>{progressTarget}</span>
            </div>

            <div className="invite-progress-bar">
              <div
                className="invite-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="invite-link-card">
        <label>{t("invite.link_label", { defaultValue: "Your invite link" })}</label>

        <p className="invite-link-subtitle">
          {t("invite.link_subtitle", { defaultValue: "Share your link with friends and earn rewards." })}
        </p>

        <div className="invite-link-box">
          <p>{inviteLink}</p>

          <button type="button" onClick={copyLink}>
            <FaCopy /> {t("invite.copy_short", { defaultValue: "Copy" })}
        </button>
        </div>

        {inviteCode && <span className="invite-code">{t("invite.code_label", { defaultValue: "Code:" })} {inviteCode}</span>}
      </div>

      <div className="invite-buttons invite-buttons-3">
        <button className="whatsapp-btn" onClick={shareWhatsApp} type="button">
          <FaWhatsapp /> {t("invite.whatsapp", { defaultValue: "WhatsApp" })}
        </button>

        <button className="share-btn" onClick={shareLink} type="button">
          <FaShareAlt /> {t("invite.shareBtn", { defaultValue: "Share" })}
        </button>

        <button className="copy-btn" onClick={copyLink} type="button">
          <FaCopy /> {t("invite.copy_short", { defaultValue: "Copy" })}
        </button>
      </div>

      <div className="invite-rewards-card">
        <h3>{t("invite.rewards_title", { defaultValue: "Invite rewards" })}</h3>

        <p className="invite-rewards-subtitle">
          {t("invite.rewards_subtitle", { defaultValue: "Invite friends and unlock extra rewards." })}
        </p>

        {rewards.map((r) => {
          const done = inviteCount >= r.target;

          return (
            <div
              key={r.target}
              className={`invite-reward-row ${done ? t("invite.reward_ready", { defaultValue: "Ready" }) : t("invite.reward_in_progress", { defaultValue: "In progress" })}`}
            >
              <div className="invite-reward-badge">{r.target}</div>

              <div className="invite-reward-info">
                <strong>{r.label}</strong>
                <span>{r.reward}</span>
              </div>

              <button type="button" disabled>
                {done ? t("invite.reward_ready", { defaultValue: "Ready" }) : t("invite.reward_in_progress", { defaultValue: "In progress" })}
              </button>
            </div>
          );
        })}
      </div>

<div className="invite-claim-card">
  <img
    className="invite-claim-bg"
    src={inviteClaimGift}
    alt={t("invite.rewards_alt", { defaultValue: "Livora rewards" })}
    width={INVITE_HERO_IMAGE_WIDTH}
    height={INVITE_HERO_IMAGE_HEIGHT}
    loading="lazy"
    decoding="async"
  />

  <div className="invite-claim-text">
    <h3>{t("invite.claim_title", { defaultValue: "Have rewards ready to claim?" })}</h3>

    <p>{t("invite.claim_subtitle", { defaultValue: "Go to your journey page and claim invite rewards now." })}</p>

    <button type="button" onClick={() => navigate("/my-rank-rewards")}>
      {t("invite.open_rewards", { defaultValue: "Open rewards" })} <FaChevronLeft />
    </button>
  </div>
</div>
      <div className="invite-tip">
        {t("invite.tip", { defaultValue: "Share your link on WhatsApp, Snapchat, Facebook, or Telegram. When your friend signs up through the link, the invite is counted automatically." })}
      </div>

      <BottomNav active="profile" />
    </div>
  );
}
